// Browser input in host-local coordinates. A desktop host can supply the same signals from
// native input and idle counters. Subscribing owns all listeners, including pointer capture.
export function createBrowserInput({ target, activityTarget = target.ownerDocument, toPoint = e => {
  const b = target.getBoundingClientRect(); return { x: e.clientX - b.left, y: e.clientY - b.top };
} }) {
  return {
    subscribe(handle) {
      const listeners = [], captured = new Set();
      const on = (node, type, fn, options) => {
        node.addEventListener(type, fn, options);
        listeners.push(() => node.removeEventListener(type, fn, options));
      };
      // A move says what pointer it is (a touch can't hover) and whether a button is down (a drag).
      on(activityTarget, 'pointermove', e => handle({ type: 'move', ...toPoint(e), pointerId: e.pointerId, pointerType: e.pointerType, pressed: e.buttons !== 0 }), { passive: true });
      // The pointer left the page (out of the window, or a finger lifted): no pointer here until it moves again.
      on(activityTarget, 'pointerout', e => { if (!e.relatedTarget) handle({ type: 'out', pointerId: e.pointerId }); }, { passive: true });
      on(activityTarget, 'keydown', () => handle({ type: 'key' }), { capture: true });
      for (const type of ['wheel', 'pointerdown']) on(activityTarget, type, () => handle({ type: 'activity' }), { passive: true, capture: true });
      on(target, 'pointerdown', e => {
        if (!handle({ type: 'down', ...toPoint(e), pointerId: e.pointerId })) return;
        e.preventDefault(); e.stopPropagation();
        target.setPointerCapture(e.pointerId); captured.add(e.pointerId);
      }, { capture: true });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) on(target, type, e => {
        // A touch is first captured by the element under it; taking the capture over makes that
        // element lose it (and the event bubbles up here) while the target still holds it.
        if (type === 'lostpointercapture' && target.hasPointerCapture(e.pointerId)) return;
        handle({ type: type === 'pointerup' ? 'up' : 'cancel', ...toPoint(e), pointerId: e.pointerId });
        if (captured.delete(e.pointerId) && target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);
      });
      return () => {
        for (const off of listeners) off();
        for (const id of captured) if (target.hasPointerCapture(id)) target.releasePointerCapture(id);
        captured.clear();
      };
    },
  };
}
